import { useTranslation } from 'react-i18next';
import { useEstimation } from '../../hooks/useEstimation';
import { useEstimationStore } from '../../stores/estimationStore';
import { KPICard } from '../shared/KPICard';
import { Bar } from '../shared/Bar';
import { InfoTip } from '../shared/InfoTip';
import { AnomalyBanner } from '../ai/AnomalyBanner';
import { ComplianceDashboard } from '../ai/ComplianceDashboard';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import styles from './EstimationTab.module.css';

export function EstimationTab() {
  const { t } = useTranslation('estimation');
  const ha = useEstimationStore((s) => s.ha);
  const haZi = useEstimationStore((s) => s.haZi);
  const r = useEstimation();

  return (
    <div className={styles.tab}>
      <h2 className={styles.title}>
        {t('title', 'Estimation')} — {ha} ha
        {haZi > 0 && ` (${ha - haZi} ha mixte + ${haZi} ha ZI)`}
      </h2>

      {/* AI Anomaly Detection */}
      <AnomalyBanner estimationData={{ ...r, ha, ha_zi: haZi }} />

      {/* CO Warning */}
      {r.inhabitants >= 10000 && (
        <div className={styles.coWarning}>
          <span className={styles.coWarningIcon}>⚠</span>
          {t('coWarning', 'Seuil CO atteint : ≥ 10\'000 habitants — un Cycle d\'Orientation peut être requis.')}
        </div>
      )}

      {/* KPI Row */}
      <div className={styles.kpiRow}>
        <KPICard label={t('logements', 'Logements')} value={fmt(r.logements)} tooltip={t('tooltips.logements')} />
        <KPICard label={t('habitants', 'Habitants')} value={fmt(r.inhabitants)} tooltip={t('tooltips.habitants')} />
        <KPICard label={t('emplois', 'Emplois')} value={fmt(r.emplois)} tooltip={t('tooltips.emplois')} />
        <KPICard
          label={t('costTotal', 'Coût total')}
          value={fmtCHF(r.cost_total)}
          sub={`${fmtM(r.cost_low)} — ${fmtM(r.cost_high)}`}
          color="var(--color-primary)"
          tooltip={t('tooltips.costTotal')}
        />
      </div>

      {/* SBP Breakdown */}
      <div className={styles.section}>
        <h3>{t('sbpBreakdown', 'Programme SBP')}</h3>
        <div className={styles.sbpGrid}>
          <KPICard label="SBP Logements" value={`${fmt(r.sbp_logements)} m²`} tooltip={t('tooltips.sbpLogements')} />
          <KPICard label="SBP Activités" value={`${fmt(r.sbp_act)} m²`} tooltip={t('tooltips.sbpActivites')} />
          <KPICard label="SBP Équipements" value={`${fmt(r.sbp_eq)} m²`} tooltip={t('tooltips.sbpEquipements')} />
          <KPICard label="SBP Total" value={`${fmt(r.sbp_total)} m²`} color="var(--color-info)" tooltip={t('tooltips.sbpTotal')} />
        </div>
      </div>

      {/* Equipment */}
      <div className={styles.section}>
        <h3>{t('equipment', 'Équipements')}</h3>
        <div className={styles.equipGrid}>
          <div className={styles.equipItem}>
            <span className={styles.equipValue}>{r.classes}</span>
            <span className={styles.equipLabel}>{t('classes', 'Classes')}<InfoTip text={t('tooltips.classes')} /></span>
          </div>
          <div className={styles.equipItem}>
            <span className={styles.equipValue}>{r.creches}</span>
            <span className={styles.equipLabel}>{t('creches', 'Crèches')}<InfoTip text={t('tooltips.creches')} /></span>
            <span className={styles.equipSub}>{r.creche_places} {t('places', 'places')}</span>
          </div>
          <div className={styles.equipItem}>
            <span className={styles.equipValue}>{r.co}</span>
            <span className={styles.equipLabel}>CO<InfoTip text={t('tooltips.co')} /></span>
          </div>
          <div className={styles.equipItem}>
            <span className={styles.equipValue}>{r.sport}</span>
            <span className={styles.equipLabel}>{t('sportFacility', 'Sport')}<InfoTip text={t('tooltips.sport')} /></span>
          </div>
          <div className={styles.equipItem}>
            <span className={styles.equipValue}>{r.culturel}</span>
            <span className={styles.equipLabel}>{t('cultural', 'Culturel')}<InfoTip text={t('tooltips.culturel')} /></span>
          </div>
        </div>
      </div>

      {/* Cost Decomposition */}
      <div className={styles.section}>
        <h3>{t('costDecomposition', 'Décomposition des coûts')}</h3>
        <div className={styles.bars}>
          <Bar label={t('infrastructure', 'Infrastructure')} value={r.cost_infra_mix} max={r.cost_total} format={fmtCHF} color="var(--color-info)" tooltip={t('tooltips.costInfra')} />
          <Bar label={t('schools', 'Écoles')} value={r.cost_ecoles} max={r.cost_total} format={fmtCHF} color="#6db" tooltip={t('tooltips.costEcoles')} />
          <Bar label={t('daycare', 'Crèches')} value={r.cost_creches} max={r.cost_total} format={fmtCHF} color="#a78bfa" tooltip={t('tooltips.costCreches')} />
          <Bar label="CO" value={r.cost_co} max={r.cost_total} format={fmtCHF} color="#e87" tooltip={t('tooltips.costCO')} />
          <Bar label={t('sport', 'Sport')} value={r.cost_sport} max={r.cost_total} format={fmtCHF} color="#22d3ee" tooltip={t('tooltips.costSport')} />
          <Bar label={t('cultural', 'Culturel')} value={r.cost_culturel} max={r.cost_total} format={fmtCHF} color="#c9a" tooltip={t('tooltips.costCulturel')} />
          <Bar label={t('publicSpaces', 'Espaces publics')} value={r.cost_ep_mix} max={r.cost_total} format={fmtCHF} color="#6366f1" tooltip={t('tooltips.costEP')} />
          {r.zi && (
            <Bar label={t('zi', 'Zone industrielle')} value={r.zi.cost_total} max={r.cost_total} format={fmtCHF} color="#aaa" tooltip={t('tooltips.costZI')} />
          )}
        </div>
      </div>

      {/* Density metrics */}
      <div className={styles.section}>
        <h3>{t('density', 'Densité')}</h3>
        <div className={styles.densityRow}>
          <KPICard label={t('logPerHa', 'Logements/ha')} value={r.density} tooltip={t('tooltips.logPerHa')} />
          <KPICard label="IUS" value={r.ius_implied.toFixed(2)} tooltip={t('tooltips.ius')} />
          <KPICard label={t('publicSpace', 'Espaces publics')} value={`${fmt(r.espaces_publics)} m²`} tooltip={t('tooltips.publicSpace')} />
        </div>
      </div>

      {/* Regulatory Compliance */}
      <div className={styles.section}>
        <h3>{t('compliance', 'Conformité réglementaire')}</h3>
        <ComplianceDashboard estimationData={{ ...r, ha, ha_zi: haZi }} />
      </div>
    </div>
  );
}
